import type { paths } from '@joyful/openapi/types.gen'
import type { Middleware } from 'openapi-fetch'
import { getCurrentJoyfulApp } from './appRuntimeStore'
import { createApiSessionCookieSync } from './apiSessionCookie'

import createFetchClient from 'openapi-fetch'
import createClient from 'openapi-react-query'

export interface CreateJoyfulApiClientOptions {
  baseUrl: string
  fetch?: typeof fetch
  getImpersonatedRole?: () => string | null
  getToken: () => Promise<string | null>
}

const ROLE_OVERRIDE_HEADER = 'X-Joyful-Role-Override'

export class FetchError extends Error {
  response: Response
  body: unknown

  constructor({ body, response }: { body: unknown; response: Response }) {
    super(`HTTP error ${response.status}`)
    this.name = 'FetchError'
    this.response = response
    this.body = body
  }
}

export function createAuthenticatedFetch(
  options: Pick<
    CreateJoyfulApiClientOptions,
    'fetch' | 'getImpersonatedRole' | 'getToken'
  >,
) {
  const fetchImplementation = options.fetch ?? fetch
  const authenticatedFetch: typeof fetch = async (input, init) => {
    const token = await options.getToken()
    const headers = new Headers(
      input instanceof Request ? input.headers : init?.headers,
    )
    if (input instanceof Request && init?.headers) {
      new Headers(init.headers).forEach((value, key) => {
        headers.set(key, value)
      })
    }

    setAuthHeaders({
      getImpersonatedRole: options.getImpersonatedRole,
      headers,
      token,
    })

    return fetchImplementation(input, {
      ...init,
      credentials: 'omit',
      headers,
    })
  }

  return authenticatedFetch
}

export function createJoyfulApiClient(options: CreateJoyfulApiClientOptions) {
  const fetchClient = createFetchClient<paths>({
    baseUrl: options.baseUrl,
    credentials: 'omit',
    fetch: options.fetch,
  })

  fetchClient.use(errorHandlingMiddleware)
  fetchClient.use(createAuthMiddleware(options))

  return {
    $api: createClient(fetchClient),
    authenticatedFetch: createAuthenticatedFetch(options),
    baseUrl: options.baseUrl,
    createAuthenticatedFetch: () => {
      return createAuthenticatedFetch(options)
    },
    fetchClient,
  }
}

export type JoyfulApiClient = ReturnType<typeof createJoyfulApiClient>

export const $api = new Proxy(
  // SAFETY: empty target; get trap forwards every property to the current app client.
  {} as JoyfulApiClient['$api'],
  {
    get(_target, property, receiver) {
      // oxlint-disable-next-line typescript/no-unsafe-return
      return Reflect.get(
        getCurrentJoyfulApp<{ api: JoyfulApiClient }>().api.$api,
        property,
        receiver,
      )
    },
  },
)

export const fetchClient = new Proxy(
  // SAFETY: empty target; get trap forwards every property to the current app client.
  {} as JoyfulApiClient['fetchClient'],
  {
    get(_target, property, receiver) {
      // oxlint-disable-next-line typescript/no-unsafe-return
      return Reflect.get(
        getCurrentJoyfulApp<{ api: JoyfulApiClient }>().api.fetchClient,
        property,
        receiver,
      )
    },
  },
)

const errorHandlingMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.ok) {
      return
    }

    const body = await response
      .clone()
      .json()
      .catch(() => {
        return response.text()
      })
    throw new FetchError({ body, response })
  },
}

/**
 * Authenticates generated-client requests.
 *
 * A GET sends the host-only API session cookie instead of the Authorization header
 * when the cookie holds the current token. A request with no custom headers skips the
 * CORS preflight. Everything else sends the header. A cookie request the API rejects
 * is retried once with the header, and the cookie path stays off for the page.
 */
function createAuthMiddleware(
  options: Pick<
    CreateJoyfulApiClientOptions,
    'baseUrl' | 'fetch' | 'getImpersonatedRole' | 'getToken'
  >,
): Middleware {
  const fetchImplementation = options.fetch ?? fetch
  const apiSessionCookie = createApiSessionCookieSync({
    baseUrl: options.baseUrl,
    fetchImplementation,
  })
  const cookieAuthenticatedRequests = new WeakSet<Request>()

  return {
    async onRequest({ request }) {
      const token = await options.getToken()
      apiSessionCookie.sync(token)
      const isImpersonating = Boolean(options.getImpersonatedRole?.())
      const canUseCookie =
        request.method === 'GET' &&
        !isImpersonating &&
        apiSessionCookie.canAuthenticate(token)
      if (canUseCookie) {
        const cookieRequest = new Request(request, { credentials: 'include' })
        cookieAuthenticatedRequests.add(cookieRequest)
        return cookieRequest
      }

      setAuthHeaders({
        getImpersonatedRole: options.getImpersonatedRole,
        headers: request.headers,
        token,
      })

      return request
    },
    async onResponse({ request, response }) {
      const isRejectedCookieRequest =
        response.status === 401 && cookieAuthenticatedRequests.has(request)
      if (!isRejectedCookieRequest) {
        return undefined
      }

      apiSessionCookie.disable()
      const token = await options.getToken()
      const headerRequest = new Request(request, { credentials: 'omit' })
      setAuthHeaders({
        getImpersonatedRole: options.getImpersonatedRole,
        headers: headerRequest.headers,
        token,
      })
      return fetchImplementation(headerRequest)
    },
  }
}

function setAuthHeaders({
  getImpersonatedRole,
  headers,
  token,
}: {
  getImpersonatedRole?: () => string | null
  headers: Headers
  token: string | null
}) {
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const impersonatedRole = getImpersonatedRole?.() ?? null
  if (impersonatedRole) {
    headers.set(ROLE_OVERRIDE_HEADER, impersonatedRole)
  }
}
